/**
 * Encapsulates long-task lifecycle concerns for an active chat: loads tool
 * metadata, re-announces terminal jobs whose directive never reached the message
 * list, and marks terminal completions as seen once the user views them. Uses the
 * tasks React Query cache (`useTasksQuery`) as the source of truth.
 */
import React from "react";
import { useAtomValue } from "jotai";

import * as types from "types";
import type { McpConfig } from "types/Mcp";
import type { StreamStatus } from "services/cortevaChain/hooks/useChatStream";
import { useEnqueueInjection } from "hooks/tasks/usePendingInjections";
import {
  longTaskGroupsLoadAtom,
  mcpConfigSignature,
  useLongTaskGroups,
} from "hooks/tasks/useLongTaskGroups";
import {
  buildTerminalInjectionForTask,
  findStrandedTerminalEntries,
} from "hooks/tasks/longTaskBuilders";
import useTasksQuery from "services/dbApi/hooks/useTasksQuery";
import { useMarkTerminalSeen } from "hooks/tasks/useMarkTerminalSeen";

/**
 * True when the committed message list already holds a terminal directive for
 * this job. Persisted the moment it drains (commitMessages); its presence is the
 * durable, multi-device inject-once signal.
 */
export function hasCommittedTerminalDirective(
  messages: types.Message[],
  jobId: string
): boolean {
  return messages.some(
    (m) =>
      types.isSyntheticUserMessage(m) &&
      m.kind === "long-task-terminal" &&
      m.longTaskJobId === jobId
  );
}

interface UseChatLongTaskLifecycleOptions {
  threadId: string;
  mcpConfig: McpConfig | undefined;
  getAccessToken: () => Promise<string>;
  messages: types.Message[];
  streamStatus: StreamStatus;
}

export function useChatLongTaskLifecycle({
  threadId,
  mcpConfig,
  getAccessToken,
  messages,
  streamStatus,
}: UseChatLongTaskLifecycleOptions): void {
  const enqueueInjection = useEnqueueInjection();
  const markTerminalSeen = useMarkTerminalSeen();

  // Reads the committed message list from the recovery effect without depending
  // on it: `setMessages` fires a new array per streamed token, and the recovery
  // runs once per resolved config signature, not per token.
  const readMessages = React.useEffectEvent(() => messages);

  const tasksQuery = useTasksQuery();

  useLongTaskGroups(mcpConfig, getAccessToken);
  const groupsLoad = useAtomValue(longTaskGroupsLoadAtom);
  const activeConfigSignature = mcpConfigSignature(mcpConfig);
  // Settled is enough here, failed or not: a failed load leaves the default
  // directive wording in place, which is worse than the server's own template but
  // far better than never announcing a stranded terminal at all.
  const longTaskGroupsSettled = groupsLoad?.signature === activeConfigSignature;

  const recoveredRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    // The lifecycle map gates this because the recovered directive's wording comes
    // from the submit tool's `directiveTemplate`; running before the map hydrates
    // would announce the completion with the built-in default text instead.
    if (!longTaskGroupsSettled) return;
    // A failed tasks GET is not the same as one still in flight: `useTasksQuery`
    // sets `retry: false`, so `data` stays undefined and the gate below would
    // skip recovery for the whole mount without ever saying why. Log it, so a
    // missing stranded-terminal recovery is diagnosable rather than silent. The
    // route's focus refetch is the recovery path.
    if (tasksQuery.isError) {
      console.error(
        "Tasks list unavailable; skipping stranded-directive recovery for this thread:",
        tasksQuery.error
      );
      return;
    }
    // Also wait for the tasks query to resolve. The gate above tracks a
    // different request (`POST /chat/tools`), which can settle first; running
    // against an empty job list would silently skip stranded-terminal recovery
    // for the whole mount, since `recoveredRef` blocks any retry.
    if (tasksQuery.data === undefined) return;
    if (recoveredRef.current === activeConfigSignature) return;
    recoveredRef.current = activeConfigSignature;

    const messages = readMessages();
    const stranded = findStrandedTerminalEntries(
      tasksQuery.data,
      threadId,
      (jobId) => hasCommittedTerminalDirective(messages, jobId)
    );

    for (const job of stranded) {
      enqueueInjection(
        threadId,
        buildTerminalInjectionForTask(job, job.status, job.statusReason ?? null)
      );
    }
    // `threadId` is intentionally omitted: recovery runs once per resolved config
    // signature (guarded by recoveredRef), not per thread switch. The remaining
    // value is a memoized callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    longTaskGroupsSettled,
    activeConfigSignature,
    tasksQuery.data,
    // `retry: false` means a failure leaves `data` undefined, so without this the
    // error transition changes no dependency and the branch above never runs.
    tasksQuery.isError,
    enqueueInjection,
  ]);

  // Marks the active chat's terminal completions as seen once the stream ends.
  // Depends on the cache its own writes feed, so it relies on `markTerminalSeen`
  // stamping that cache up front to avoid re-PATCHing on every response.
  React.useEffect(() => {
    if (streamStatus === "loading") return;
    markTerminalSeen(tasksQuery.data ?? [], threadId);
  }, [threadId, streamStatus, tasksQuery.data, markTerminalSeen]);
}
